import { PlayerConfig } from '@/interfaces/player-config.interface';
import { logService } from './log.service';
import { videoOnly } from '@/ads/ad-url';

export class AdService {
  /**
   * Ensures description_url parameter is properly set in the ad server URL
   * Replaces placeholders, empty values, or anything in brackets with the actual page URL
   */
  private ensureDescriptionUrl(adserverUrl: string, pageUrl: string): string {
    let url = adserverUrl;

    // Check if description_url parameter exists
    const descUrlMatch = url.match(/description_url=([^&]*)/);

    if (descUrlMatch) {
      const descUrlValue = descUrlMatch[1];

      // Replace if it contains brackets, is empty, or is a placeholder
      if (!descUrlValue || descUrlValue.includes('[') || descUrlValue.includes(']')) {
        url = url.replace(/description_url=[^&]*/, `description_url=${pageUrl}`);
      }
    } else {
      // If description_url doesn't exist, add it
      const separator = url.includes('?') ? '&' : '?';
      url = url + separator + `description_url=${pageUrl}`;
    }

    return url;
  }

  /**
   * Note: We intentionally do NOT manage the 'url' parameter.
   * The Google IMA SDK adds and manages it automatically:
   * - For production URLs: IMA SDK uses the actual page referrer
   * - For localhost URLs: IMA SDK sets it to 'null' (this is expected behavior)
   * - The SDK also transforms: output=vast → output=xml_vast4
   * - And changes domain: pubads.g.doubleclick.net → pagead2.googlesyndication.com
   */

  private buildCustomParams(playerConfig: PlayerConfig, adType?: string): string {
    const type = playerConfig.live ? 'live' : 'clip';

    let custParams =
      'sourceId=' + playerConfig.sourceId +
      '&type=' + type +
      '&accountId=' + playerConfig.accountId +
      '&width=' + window.innerWidth +
      '&height=' + window.innerHeight;

    // Add targeting tag if available
    if (playerConfig.adTargetTag) {
      custParams += '&targettag=' + playerConfig.adTargetTag;
    }

    // Add ad type for specific targeting
    if (adType) {
      custParams += '&adtype=' + adType;
    }

    return custParams;
  }

  public getPrerollUrl(playerConfig: PlayerConfig): string {
    const custParams = this.buildCustomParams(playerConfig, 'preroll');

    // Get the actual page URL where the player is loaded (parent page URL in iframe mode)
    const pageUrl = encodeURIComponent(playerConfig.parentFullUrl);

    logService.trace('[AdService] Original adserver URL:', playerConfig.advertisement.adserver);
    logService.trace('[AdService] Page URL (encoded):', pageUrl);

    // Ensure description_url is properly set
    let adserver = this.ensureDescriptionUrl(playerConfig.advertisement.adserver, pageUrl);
    logService.trace('[AdService] After ensureDescriptionUrl:', adserver);

    // Add vpos parameter for ad position
    adserver = videoOnly(adserver) + '&vpos=preroll';

    // Note: We intentionally do NOT set the 'url' parameter here.
    // The Google IMA SDK adds it automatically and manages it internally.
    // If we try to set it to localhost URLs, the IMA SDK overrides it with 'null'.
    // For production URLs, the IMA SDK will use the actual page referrer.

    // Build the complete URI with custom parameters
    const uri = adserver + '&cust_params=' + encodeURIComponent(custParams);

    logService.debug('[AdService] Preroll URL generated:', uri);
    return uri;
  }

  public getMidrollUrl(playerConfig: PlayerConfig, midrollIndex: number = 1): string {
    const custParams = this.buildCustomParams(playerConfig, 'midroll');

    // Get the actual page URL where the player is loaded (parent page URL in iframe mode)
    const pageUrl = encodeURIComponent(playerConfig.parentFullUrl);

    // Replace preroll with midroll in the adserver URL
    let midrollUrl = playerConfig.advertisement.adserver;
    if (midrollUrl.includes('etv_preroll')) {
      midrollUrl = midrollUrl.replace('etv_preroll', 'etv_midroll');
    } else if (midrollUrl.includes('/1052246/')) {
      // Fallback: if the URL pattern is different, use the hardcoded midroll URL
      midrollUrl = 'https://pubads.g.doubleclick.net/gampad/live/ads?iu=/1052246/etv_midroll&description_url=[referrer_url]&env=vp&impl=s&correlator=&tfcd=0&npa=1&gdfp_req=1&output=vast&sz=1280x720&min_ad_duration=5000&max_ad_duration=30000&unviewed_position_start=1';
    }

    // Ensure description_url is properly set
    midrollUrl = this.ensureDescriptionUrl(midrollUrl, pageUrl);

    // Add vpos and mridx parameters for midroll tracking
    midrollUrl = videoOnly(midrollUrl) + `&vpos=midroll&mridx=${midrollIndex}`;

    // Note: We intentionally do NOT set the 'url' parameter.
    // The Google IMA SDK manages it automatically.

    // Build the complete URI with custom parameters
    const uri = midrollUrl + '&cust_params=' + encodeURIComponent(custParams);

    logService.debug(`[AdService] Midroll #${midrollIndex} URL generated:`, uri);
    return uri;
  }

  public getPostrollUrl(playerConfig: PlayerConfig): string {
    const custParams = this.buildCustomParams(playerConfig, 'postroll');

    // Get the actual page URL where the player is loaded (parent page URL in iframe mode)
    const pageUrl = encodeURIComponent(playerConfig.parentFullUrl);

    // Replace preroll with postroll in the adserver URL
    let postrollUrl = playerConfig.advertisement.adserver;
    if (postrollUrl.includes('etv_preroll')) {
      postrollUrl = postrollUrl.replace('etv_preroll', 'etv_postroll');
    } else {
      // Fallback: create postroll URL pattern
      postrollUrl = postrollUrl.replace('/1052246/', '/1052246/etv_postroll');
    }

    // Ensure description_url is properly set
    postrollUrl = this.ensureDescriptionUrl(postrollUrl, pageUrl);

    // Add vpos parameter for ad position
    postrollUrl = videoOnly(postrollUrl) + '&vpos=postroll';

    // Note: We intentionally do NOT set the 'url' parameter.
    // The Google IMA SDK manages it automatically.

    // Build the complete URI with custom parameters
    const uri = postrollUrl + '&cust_params=' + encodeURIComponent(custParams);

    logService.debug('[AdService] Postroll URL generated:', uri);
    return uri;
  }

  public shouldLoadAds(playerConfig: PlayerConfig): boolean {
    if (!playerConfig.advertisement?.enabled) {
      return false;
    }

    // Check if any of the ad types are enabled in the template
    // Note: We'll need to check against the EtvConfig template.advertisement structure
    return true; // For now, return true if advertisement is enabled
  }
}

export const adService = new AdService();
